import { cn } from '@/lib/utils'
import * as React from 'react'

interface TableProps extends React.ComponentProps<'table'> {
  containerClassName?: string
  // Ref to the scrolling container div (the element with overflow-auto).
  // The inner <table> ref still goes through the standard `ref` prop.
  containerRef?: React.Ref<HTMLDivElement>
}

function Table({
  className,
  containerClassName,
  containerRef,
  onScroll,
  ...props
}: TableProps) {
  return (
    <div
      ref={containerRef}
      className={cn('w-full overflow-auto', containerClassName)}
      // REMINDER: we are not scrolling the table, but the container
      {...{ onScroll }}>
      <table
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      className={cn(
        '[&_tr]:border-neutral-subtle-border [&_tr]:border-b',
        className
      )}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      className={cn(
        'bg-primary text-primary-foreground font-medium',
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'border-neutral-subtle-border border-b transition-colors',
        'hover:bg-primary-subtle-bg/60',
        'data-[state=selected]:bg-primary-active-bg data-[state=selected]:hover:bg-primary-active-bg',
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'text-neutral-low-contrast h-11 px-4 text-left align-middle text-sm font-normal [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      className={cn(
        'text-primary-high-contrast px-4 py-3 align-middle text-sm [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>) {
  return (
    <caption
      className={cn('text-muted-foreground mt-4 text-sm', className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
